<template>
  <!-- Nada é renderizado sem a flag e sem a permissão de leitura no escopo da
       escala — nem skeleton, nem erro, nem toast. A permissão é
       `post_station_station_status_reading` (PRD 14 §10: Leitor Avançado e
       acima; RNF de segurança "todas as ações guardadas por permissão"). -->
  <template v-if="canReadPostStation">
    <div
      v-if="isLoading"
      class="flex flex-col gap-3 animate-pulse"
      data-testid="post-station-details-skeleton"
    >
      <div class="h-5 w-40 rounded bg-slate-200"></div>
      <div class="h-20 w-full rounded-lg bg-slate-200"></div>
    </div>

    <section
      v-else-if="row"
      class="flex flex-col gap-3 text-left"
      data-testid="post-station-details-section"
    >
      <div
        class="flex items-center gap-2 flex-wrap"
        :class="showTitle ? 'justify-between' : 'justify-end'"
      >
        <h2 v-if="showTitle" class="text-lg font-bold text-left">Pós-Plantão</h2>

        <OutlineButton
          class="gap-1"
          height="32px"
          width="152px"
          data-testid="post-station-details-open-sidebar-button"
          @click="openSidebar"
        >
          <SVGIcon :path="mdiOpenInNew" class="w-4 h-4" />
          Ver detalhes
        </OutlineButton>
      </div>

      <!-- Banner de trava (RF-12): informativo apenas. O bloqueio efetivo da
           edição é da US-04. O cadeado deste banner é de PÓS-PLANTÃO — não
           confundir com o cadeado de excepcionalidade (`mdiLockOpenVariant`,
           vermelho) usado no resto do detalhe do plantão; por isso a cor
           institucional e o rótulo "Pós-Plantão" explícito no texto. -->
      <div
        v-if="isApproved"
        class="flex items-start gap-2 px-2 py-1 rounded-md border border-primary30 bg-primary5 text-sm text-textPrimary"
        data-testid="post-station-details-lock-banner"
      >
        <SVGIcon :path="mdiInformation" class="w-4 h-4 mt-0.5 shrink-0 text-primary30" />
        <span>{{ POST_STATION_LOCK_BANNER_TEXT }}</span>
      </div>

      <!-- Callout de reversão (RF-23): acima dos pilares, aberto por padrão.
           Gated pelo status REVERTED, não só pela existência da reversão — depois
           de revalidar e aprovar, `lastReversion` continua vindo na resposta
           (fica no Histórico) e o callout precisa desaparecer. -->
      <PostStationRevertedCallout
        v-if="isReverted && lastReversion"
        :reversion="lastReversion"
        :timezone="stationTimezone"
      />

      <div
        class="grid grid-cols-3 md:grid-cols-6 gap-x-3 gap-y-4 rounded-lg py-3 px-3 md:px-6"
        style="
          border-radius: 8px;
          border: 1px solid var(--Neutral-LightGray-30, #d8d8e2);
          background: var(--White, #fff);
        "
      >
        <div
          v-for="domain in row.detail.domains"
          :key="domain.key"
          class="flex flex-col items-center gap-2 min-w-0"
          :data-testid="`post-station-details-domain-${domain.key}`"
        >
          <!-- Rótulo curto do mockup; o nome completo do domínio (DOMAIN_LABELS)
               fica no title e no tooltip do ComplianceIcon. -->
          <span
            class="text-xs md:text-sm font-bold text-center w-full truncate"
            :title="domainTitle(domain)"
          >
            {{ SHORT_DOMAIN_LABELS[domain.key] }}
          </span>
          <!-- F4 (M5, balão ①) — só "Reg. Plantão" ganha os quatro estados, e
               só quando `resolveRegistrationCellStatus` autoriza (a MESMA regra
               da célula do grid: sem política vigente, campo ausente ou valor
               desconhecido caem na bolinha de conformidade, que já diz "Não
               avaliado"). Os outros quatro domínios passam `null` e ficam
               exatamente como estão. -->
          <ComplianceIcon
            :state="domain.state"
            :domain-label="domain.label"
            :registration-status="registrationStatusFor(domain)"
          />
        </div>

        <div
          class="flex flex-col items-center gap-2 min-w-0"
          data-testid="post-station-details-status"
        >
          <span class="text-xs md:text-sm font-bold text-center w-full truncate">Status</span>
          <PostStationStatusBadge :status="row.status" />
        </div>
      </div>
    </section>

    <!-- A sidebar é `position: fixed`: dentro do modal ela herdaria o
         `transform` de `.modal-pushed-by-sidebar` e o overflow do corpo do
         modal. Teleport para `#app` (mesmo padrão do ValueDetailsSection). -->
    <Teleport v-if="row" to="#app">
      <PostStationDetailSidebar
        :model-value="isSidebarOpen"
        :row="row"
        :evaluation-id="row.evaluationId"
        @update:model-value="setSidebarOpen"
      />
    </Teleport>
  </template>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { mdiInformation, mdiOpenInNew } from '@mdi/js'

import SVGIcon from '@/components/SVGIcon.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import Network from '@/Network'
import type { PostStationStationEvaluationResponse } from '@/Network/Types/Responses/PostStationResponses'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import PostStationRevertedCallout from '@/modules/company/Dashboard/components/PostStationRevertedCallout.vue'
import ComplianceIcon from '@/modules/company/PostStation/components/ComplianceIcon.vue'
import PostStationDetailSidebar from '@/modules/company/PostStation/components/PostStationDetailSidebar.vue'
import PostStationStatusBadge from '@/modules/company/PostStation/components/PostStationStatusBadge.vue'
import {
  COMPLIANCE_STATE_TITLES,
  POST_STATION_LOCK_BANNER_TEXT,
  registrationCellTitle,
  resolveRegistrationCellStatus
} from '@/modules/company/PostStation/constants'
import { mapEvaluationToRow } from '@/modules/company/PostStation/mappers'
import { PostStationStatus } from '@/modules/company/PostStation/types'
import type {
  PostStationCompliance,
  PostStationDomainDetail
} from '@/modules/company/PostStation/types'

const props = withDefaults(
  defineProps<{
    stationId: number
    scheduleId: number
    /**
     * Avaliação já buscada pelo pai. Distingue três estados de propósito:
     *
     * - `undefined` — não fornecida: o componente faz o próprio fetch (caminho
     *   original, ainda usado por quem montar a seção isolada).
     * - `null` — fornecida e não existe avaliação (404).
     * - objeto — fornecida.
     *
     * Existe para o `stationDetailsModal` não pagar um segundo GET do mesmo
     * endpoint: ele já precisa da avaliação antes de renderizar, para decidir o
     * estado inicial do accordion de "Registro de plantão".
     */
    evaluation?: PostStationStationEvaluationResponse | null
    /**
     * Título "Pós-Plantão" do card. Desligado quando a seção mora dentro de um
     * accordion que já exibe o mesmo título no cabeçalho.
     */
    showTitle?: boolean
  }>(),
  { evaluation: undefined, showTitle: true }
)

const emit = defineEmits<{
  // O modal precisa saber para deslizar (classe `modal-pushed-by-sidebar`).
  sidebarToggle: [isOpen: boolean]
}>()

// Rótulos curtos das colunas do card (o card tem 6 colunas dentro do modal, não
// cabe o nome completo). Local de propósito: `constants.ts` só tem os rótulos
// longos (DOMAIN_LABELS) e as labels do grid do painel são chaveadas por coluna.
const SHORT_DOMAIN_LABELS: Record<keyof PostStationCompliance, string> = {
  registration: 'Reg. Plantão',
  qualification: 'Doc. Qual.',
  repass: 'Repasse',
  exception: 'Excep.',
  // "Pol. Pgto." e não "Pgto.": o domínio é a POLÍTICA de pagamento
  // (`DOMAIN_LABELS.payment`), e o rótulo curto anterior se lia como status de
  // pagamento do plantão.
  payment: 'Pol. Pgto.'
}

const { flags } = useFeatureFlags()
const permissionsStore = usePermissionsStore()

// Mesmo padrão de `checkSchedulePermission` do stationDetailsModal: a permissão
// de leitura do status é concedida no escopo da escala do plantão.
const canReadPostStation = computed(() => {
  if (!flags.enabledPostStation || !props.scheduleId) return false
  return permissionsStore.check({
    permission: AuthorizationPermissions.PostStationStationStatusReading,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.Schedule,
      objectId: props.scheduleId
    }
  })
})

// `lastReversion` vem junto na resposta e é o que o callout do RF-23 (FE-5) vai
// consumir — por isso guardamos a resposta inteira, não só a row mapeada.
const fetchedEvaluation = ref<PostStationStationEvaluationResponse | null>(null)
const isLoading = ref(false)
const isSidebarOpen = ref(false)

const setSidebarOpen = (value: boolean) => {
  isSidebarOpen.value = value
  emit('sidebarToggle', value)
}

const openSidebar = () => setSidebarOpen(true)

// Fechar o modal desmonta o corpo dele (e esta seção com a sidebar). Sem avisar,
// o pai guardaria a flag ligada e reabriria o modal deslizado sem sidebar.
onBeforeUnmount(() => {
  if (isSidebarOpen.value) setSidebarOpen(false)
})

// Guard de corrida: só a request mais recente escreve no estado (o modal é
// reaproveitado entre plantões e o stationId troca sem desmontar).
let fetchSeq = 0

const fetchEvaluation = async (stationId: number) => {
  const seq = ++fetchSeq
  isLoading.value = true
  try {
    // `null` = 404 = plantão sem avaliação de pós-plantão (estado esperado).
    const response = await Network.postStation.getStationEvaluation(stationId)
    if (seq !== fetchSeq) return
    fetchedEvaluation.value = response
  } catch (e) {
    if (seq !== fetchSeq) return
    fetchedEvaluation.value = null
    // Falha real de backend: o card não aparece (não há o que mostrar), mas o
    // erro não pode desaparecer silenciosamente.
    console.error('Falha ao buscar a avaliação de pós-plantão do plantão', e)
  } finally {
    if (seq === fetchSeq) isLoading.value = false
  }
}

// `canReadPostStation` entra como fonte porque as permissões chegam por request:
// o primeiro run pode ver `false` e liberar depois que o store carrega.
watch(
  [() => props.stationId, canReadPostStation],
  ([stationId, canRead]) => {
    fetchedEvaluation.value = null
    // Trocar de plantão desmonta a sidebar: avisa o modal para voltar à posição.
    if (isSidebarOpen.value) setSidebarOpen(false)
    // Avaliação vinda por prop: o pai já buscou, não repetir a request.
    if (props.evaluation !== undefined) {
      isLoading.value = false
      return
    }
    if (!canRead || !stationId) {
      fetchSeq += 1
      isLoading.value = false
      return
    }
    fetchEvaluation(stationId)
  },
  { immediate: true }
)

// Fonte única para o resto do componente: a prop quando fornecida, senão o que
// o fetch próprio trouxe.
const resolvedEvaluation = computed(() =>
  props.evaluation !== undefined ? props.evaluation : fetchedEvaluation.value
)

const row = computed(() =>
  resolvedEvaluation.value ? mapEvaluationToRow(resolvedEvaluation.value) : null
)

const isApproved = computed(() => row.value?.status === PostStationStatus.APPROVED)

const isReverted = computed(() => row.value?.status === PostStationStatus.REVERTED)

const lastReversion = computed(() => resolvedEvaluation.value?.lastReversion ?? null)

// Offset ISO do plantão (`-03:00`), vindo de `stations.timezone` no backend.
const stationTimezone = computed(() => resolvedEvaluation.value?.timezone ?? null)

/**
 * F4 — o estado de quatro valores é EXCLUSIVO do domínio de registro, e a
 * decisão de pintá-lo é a mesma da célula do grid: `resolveRegistrationCellStatus`
 * devolve `null` para escala sem política vigente
 * (`registrationPolicyInEffect === false`), campo ausente (backend anterior à
 * T9) e valor desconhecido — e aí o card volta à bolinha de conformidade, que
 * já diz "Não avaliado". Sem a `row` não há o que resolver.
 */
const registrationStatusFor = (domain: PostStationDomainDetail) => {
  if (domain.key !== 'registration' || !row.value) return null
  return resolveRegistrationCellStatus(row.value)
}

const domainTitle = (domain: PostStationDomainDetail) => {
  if (domain.key === 'registration' && row.value) {
    return `${domain.label} — ${registrationCellTitle(row.value, domain.state)}`
  }
  return `${domain.label} — ${COMPLIANCE_STATE_TITLES[domain.state]}`
}
</script>
